import { dateFormat, empty2Undefined } from "@/utils";
import type { WharfEntity, WharfRow } from "./type";
import { DATETIME_PICKER_FORMAT, DEFAULT_VALUE } from "@/config";
import { areaLib } from "@/utils/areaLib";
import { throttle } from "lodash-es";
import type { Suggest, Poi, TiandituSearch } from "@zhihaoscm/entity";
import { tiandituService } from "@/api/services/tiandituService";

/**后端数据转前端 */
export const entity2Type = (entity: WharfEntity): WharfRow => {
    return {
        id: entity.id,
        name: entity.name,
        shortName: entity.shortName,
        address: areaLib.getFullAreaName(entity.provinceCode, entity.cityCode, entity.regionCode) + entity.address,
        area: entity.area,
        desc: entity.remark,
        updateDate: dateFormat(entity.updatedTime, DATETIME_PICKER_FORMAT),
        province: entity.provinceCode,
        city: entity.cityCode,
        region: entity.regionCode,
        detailAddress: entity.address,
        lngLat: entity.latLon,
        contactName: entity.contactName,
        contactTel: entity.phone,
        fullAddress: entity.fullAddress
    } as WharfRow;
};

/**前端数据转后端 */
export const type2Entity = (type: Partial<WharfRow>): WharfEntity => {
    return empty2Undefined({
        name: type.name,
        shortName: type.shortName,
        address: type.detailAddress,
        area: type.area,
        contactName: type.contactName,
        phone: type.contactTel,
        provinceCode: type.province,
        cityCode: type.city,
        regionCode: type.region,
        latLon: type.lngLat,
        remark: type.desc,
        fullAddress: areaLib.getFullAreaName(type.province, type.city, type.region) + type.detailAddress
    } as WharfEntity);
};
/** 联系人信息*/
export const getContactInfo = (rowData: Partial<WharfRow>) => {
    let info: string;
    if (rowData.contactName && rowData.contactTel) {
        info = `${rowData.contactName}   ${rowData.contactTel}`;
    } else if (!rowData.contactName && rowData.contactTel) {
        info = `${rowData.contactTel}`;
    } else {
        info = DEFAULT_VALUE;
    }
    return info;
};

/**
 * 天地图 queryType=7 时，`specify` 传区县国标码更稳定；若仅有市级编码，则从地区库取该市下第一个区县码。
 * 取不到时返回空串，由 {@link onAddressInput} 走 queryType=4（不按行政区限定）。
 */
export function resolveTiandituSpecifyFromCityAdcode(cityAdcode: string): string {
    const cid = cityAdcode?.trim() ?? "";
    if (!cid) return "";
    const root = areaLib.areaData as { id: string; childs?: { id: string; childs?: { id: string }[] }[] }[] | undefined;
    if (!root?.length) return "";
    for (const prov of root) {
        for (const city of prov.childs ?? []) {
            if (String(city.id) !== cid) continue;
            const firstCounty = city.childs?.[0];
            if (firstCounty?.id != null) return String(firstCounty.id);
            return "";
        }
    }
    return "";
}

/** 执行一次地址查询；独立表单实例可自行防抖，避免共享节流状态互相吞掉请求。 */
export const queryAddressSuggestions = async (val: string, cityRegionCode: string) => {
    if (val === undefined || val === "") {
        return [];
    }
    const specify = cityRegionCode?.trim();
    const params: TiandituSearch = {
        keyWord: val,
        mapBound: "-180,-90,180,90",
        level: 18,
        // queryType为7：地名搜索，4为精确查询poi名称（不传省市区编码specify）
        queryType: specify ? "7" : "4",
        // show为2，返回详细poi信息
        show: "2",
        start: "0",
        count: "10"
    };
    if (specify) params.specify = specify;
    try {
        const { pois, suggests } = await tiandituService.search(params);
        // 天地图可能同时返回空 suggests 与非空 pois，空建议列表不能覆盖可用 POI 结果。
        const result: Suggest[] | Poi[] | undefined = suggests?.length ? suggests : pois;
        if (result) {
            return result.map((item: Suggest | Poi) => {
                let [lon, lat] = item.lonlat.split(",");
                lon = Number(lon).toFixed(6);
                lat = Number(lat).toFixed(6);
                return {
                    value: "name" in item ? item.name : item.address,
                    link: {
                        ...item,
                        lonlat: `${lon}, ${lat}`
                    }
                };
            });
        }
        return [];
    } catch (error) {
        console.error("码头选项加载失败", error);
    }
};

/** 兼容旧页面的共享节流入口；新 React 控件使用实例级防抖调用原始查询。 */
export const onAddressInput = throttle(queryAddressSuggestions, 100);

/**截取编码后6位，回填省市区 */
export const sliceGbCode = (gbCode: string) => {
    const lastSix = gbCode.slice(-6);
    const arr = [];
    for (let i = 2; i <= lastSix.length; i += 2) {
        arr.push(lastSix.slice(0, i));
    }
    return arr;
};
